import { useQuery } from "@apollo/client/react";
import { format } from "date-fns";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CHART_UI_COLORS, DATA_VIZ_COLORS } from "../../../config/chartColors";
import { RetentionRateOverTimeDocument } from "../../../generated/graphql";

interface RetentionRateChartProps {
  studentId: string;
  lessonId?: string;
  dateRange?: { start: Date; end: Date };
}

export function RetentionRateChart({ studentId, lessonId, dateRange }: RetentionRateChartProps) {
  const {
    data,
    loading: isLoading,
    error,
  } = useQuery(RetentionRateOverTimeDocument, {
    variables: {
      studentId,
      lessonId,
      dateRange,
    },
  });

  if (isLoading) {
    return (
      <div className="chart-container">
        <div className="chart-loading">Loading chart...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container">
        <div className="chart-error">Error loading chart: {error.message}</div>
      </div>
    );
  }

  if (!data?.retentionRateOverTime || data.retentionRateOverTime.length === 0) {
    return (
      <div className="chart-container">
        <div className="chart-empty">No data available</div>
      </div>
    );
  }

  const chartData = data.retentionRateOverTime.map((point) => ({
    date: format(new Date(point.date), "MMM dd"),
    retentionRate: point.retentionRate,
    totalReviews: point.totalReviews,
  }));

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h4 className="h6">Retention Rate</h4>
      </div>
      <div className="chart-body">
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_UI_COLORS.grid} />
            <XAxis dataKey="date" tick={{ fill: CHART_UI_COLORS.axis }} />
            <YAxis
              domain={[0, 100]}
              label={{
                value: "Retention Rate (%)",
                angle: -90,
                position: "insideLeft",
                fill: CHART_UI_COLORS.axis,
              }}
              tick={{ fill: CHART_UI_COLORS.axis }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: CHART_UI_COLORS.tooltip.background,
                border: `1px solid ${CHART_UI_COLORS.tooltip.border}`,
                borderRadius: "0.5rem",
              }}
              labelStyle={{ color: CHART_UI_COLORS.tooltip.label }}
              itemStyle={{ color: CHART_UI_COLORS.tooltip.text }}
              formatter={(value, name) => {
                if (name === "retentionRate" && typeof value === "number") {
                  return [`${value.toFixed(1)}%`, "Retention Rate"];
                }
                return [value, name];
              }}
            />
            <Legend wrapperStyle={{ color: CHART_UI_COLORS.legend }} />
            <Line
              type="monotone"
              dataKey="retentionRate"
              stroke={DATA_VIZ_COLORS.primary}
              strokeWidth={2}
              dot={{ r: 4 }}
              name="Retention Rate"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
